import { useState, useRef, useEffect } from 'react';
import { showToast } from './Toasts';

interface QuoteRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; phone: string; email: string; zipCode: string }) => void;
  defaultName?: string | null;
  defaultPhone?: string | null;
  defaultEmail: string | null;
  defaultZip?: string | null;
  selectedFinishName: string;
  selectedGloss: string;
  title?: string;
  subtitle?: React.ReactNode;
  ctaLabel?: string;
}

export function QuoteRequestModal({
  isOpen,
  onClose,
  onSubmit,
  defaultName,
  defaultPhone,
  defaultEmail,
  defaultZip,
  selectedFinishName,
  selectedGloss,
  title = 'Get Your Free Refinishing Estimate',
  subtitle,
  ctaLabel = 'Send My Estimate Request',
}: QuoteRequestModalProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(defaultEmail || '');
  const [zipCode, setZipCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName(defaultName || '');
      setPhone(defaultPhone || '');
      setEmail(defaultEmail || '');
      setZipCode(defaultZip || '');
      setIsSubmitting(false);
      setTimeout(() => nameRef.current?.focus(), 100);
    }
  }, [isOpen, defaultName, defaultPhone, defaultEmail, defaultZip]);

  if (!isOpen) return null;

  const isValid =
    name.trim().length >= 2 &&
    phone.trim().length >= 7 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
    /^\d{5}(-\d{4})?$/.test(zipCode.trim());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) {
      showToast('Please fill in all fields correctly', 'warning');
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), phone: phone.trim(), email: email.trim(), zipCode: zipCode.trim() });
      onClose();
    } catch {
      showToast('Failed to send request. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm mx-4 rounded-3xl glass-modal shadow-3d-card animate-modal-enter"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="mb-5 flex items-start justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-900">{title}</h3>
              <p className="mt-1 text-sm text-slate-500">
                {subtitle ?? (
                  <>
                    A specialist from AgenticPNW will contact you within 24 hours, or call us at{' '}
                    <a href="tel:+15033195182" className="font-semibold text-bss-blue hover:underline">503-319-5182</a>.
                  </>
                )}
              </p>
            </div>
            <button
              onClick={onClose}
              className="ml-2 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Selection summary */}
          <div className="mb-4 rounded-xl bg-bss-blue-50 border border-bss-blue/10 px-3 py-2">
            <p className="text-xs font-medium text-bss-blue-dark">
              Selected: <span className="font-semibold">{selectedFinishName}</span> ·{' '}
              <span className="capitalize">{selectedGloss}</span>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label htmlFor="quote-name" className="mb-1 block text-xs font-semibold text-slate-600">
                Full Name
              </label>
              <input
                ref={nameRef}
                id="quote-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                autoComplete="name"
                className="w-full rounded-xl border border-slate-200 bg-white/80 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 shadow-inner-glow focus:border-bss-blue focus:ring-2 focus:ring-bss-blue/20 focus:outline-none transition-all"
              />
            </div>

            <div>
              <label htmlFor="quote-phone" className="mb-1 block text-xs font-semibold text-slate-600">
                Phone Number
              </label>
              <input
                id="quote-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(503) 555-1234"
                autoComplete="tel"
                className="w-full rounded-xl border border-slate-200 bg-white/80 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 shadow-inner-glow focus:border-bss-blue focus:ring-2 focus:ring-bss-blue/20 focus:outline-none transition-all"
              />
            </div>

            <div>
              <label htmlFor="quote-email" className="mb-1 block text-xs font-semibold text-slate-600">
                Email
              </label>
              <input
                id="quote-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="w-full rounded-xl border border-slate-200 bg-white/80 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 shadow-inner-glow focus:border-bss-blue focus:ring-2 focus:ring-bss-blue/20 focus:outline-none transition-all"
              />
            </div>

            <div>
              <label htmlFor="quote-zip" className="mb-1 block text-xs font-semibold text-slate-600">
                Zip Code
              </label>
              <input
                id="quote-zip"
                type="text"
                inputMode="numeric"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                placeholder="97201"
                autoComplete="postal-code"
                className="w-full rounded-xl border border-slate-200 bg-white/80 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 shadow-inner-glow focus:border-bss-blue focus:ring-2 focus:ring-bss-blue/20 focus:outline-none transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={!isValid || isSubmitting}
              className="mt-2 w-full rounded-xl bg-bss-blue py-3 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-bss-blue-dark hover:shadow-blue-600/35 hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                ctaLabel
              )}
            </button>

            <p className="text-center text-[11px] text-slate-400">
              Your information is secure and will only be used to provide your estimate.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
